import {
  useState,
  createRef,
  useEffect,
  Suspense,
  lazy,
  startTransition,
} from 'react';
import PropTypes from 'prop-types';
import FocusLock, { MoveFocusInside } from 'react-focus-lock';
import { useIntl } from 'gatsby-plugin-react-intl';
import { BLOCKS } from '@contentful/rich-text-types';

import PageBuilder from '../../Core/PageBuilder';
import RichText from '../../Core/RichText';
import Loader from '../../Core/Loader';

import {
  Wrapper,
  Overlay,
  Modal,
  CloseBtn,
  Header,
  ModalButton,
  Disclaimer,
} from './styles';

const RequirementTable = lazy(
  () => import('./ModalComponents/RequirementTable'),
);
const ModalCopy = lazy(() => import('./ModalComponents/ModalCopy'));
const ModalStatements = lazy(() => import('./ModalComponents/ModalStatements'));

const disclaimer = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <Disclaimer>{children}</Disclaimer>,
    [BLOCKS.HEADING_2]: (_, children) => <Disclaimer>{children}</Disclaimer>,
  },
};

const pageTemplate = {
  'requirement-table': RequirementTable,
  'simple-paragraph': ModalCopy,
  'title-with-statement': ModalStatements,
};

const CtaModal = ({ data, className = '', as = 'div' }) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const modalRef = createRef();
  const handleOpen = state => {
    startTransition(() => setOpen(state));
  };
  const toggle = () => handleOpen(!open);
  const { linkText, sections, disclaimerText, type, ctaButtonSize } = data;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      document.body.parentElement.style.overflow = open ? 'hidden' : 'auto';
    }
  }, [open]);

  return (
    <Suspense fallback={<Loader />}>
      <Wrapper as={as} className={className} ref={modalRef}>
        <ModalButton
          className={`${className} ${type} ${ctaButtonSize}`}
          onClick={toggle}
          aria-expanded={open}
          aria-haspopup="true"
        >
          {linkText}
        </ModalButton>
        <Overlay $open={open} onClick={() => handleOpen(false)}>
          <FocusLock disabled={!open} returnFocus>
            <Modal $open={open}>
              <MoveFocusInside>
                <Wrapper>
                  <Header>
                    <CloseBtn
                      aria-label={`${intl.formatMessage({ id: 'close' })}`}
                      onClick={() => handleOpen(false)}
                    />
                  </Header>
                  <PageBuilder sections={sections} template={pageTemplate} />
                  {disclaimerText && (
                    <RichText document={disclaimerText} options={disclaimer} />
                  )}
                </Wrapper>
              </MoveFocusInside>
            </Modal>
          </FocusLock>
        </Overlay>
      </Wrapper>
    </Suspense>
  );
};

CtaModal.propTypes = {
  data: PropTypes.shape().isRequired,
  className: PropTypes.string,
  as: PropTypes.string,
};

export default CtaModal;
